import {
  ArrowLeftOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EyeOutlined,
  LockOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  notification,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import useOneM1 from '@aims/shared/shared/use-one-m1';
import useSingleSomethingA10 from '@aims/shared/shared/use-single-something-a10';
import useSingleSomethingUpdatesA10 from '@aims/shared/shared/use-single-something-updates-a10';
import CSPage from '../../components/CSPage';
import CSPageHeader from '../../components/CSPageHeader';
import {
  createRsTemplateAction,
  deleteRsTemplateAction,
  updateRsTemplateAction,
} from '../../redux-store/report-schedule-templates-store';
import { generateUuid } from '../../shared/utils';
import {
  FormFieldTypes,
  ScheduleFrequencies,
  createReportScheduleTemplateMutation,
  deleteReportScheduleTemplateMutation,
  extractReportScheduleTemplate,
  reportScheduleTemplateQuery,
  reportScheduleTemplateUpdatesSubscription,
  updateReportScheduleTemplateMutation,
} from './constants';
import PreviewTemplateFormModal from './PreviewTemplateFormModal';

const queryId = 'editReportScheduleTemplate';

const { Title, Paragraph, Text } = Typography;

function FormFieldEditor({
  field,
  index,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  readOnly = false,
  optionsError,
}) {
  const [optionsText, setOptionsText] = useState(() =>
    (field.options || []).join(', '),
  );

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Space>
          <Text strong>{`Field ${index + 1}`}</Text>
          <Text type="secondary">
            {FormFieldTypes[field.fieldType]?.label || field.fieldType}
          </Text>
        </Space>
      }
      extra={
        <Space>
          <Button
            type="text"
            size="small"
            icon={<UpOutlined />}
            disabled={readOnly || isFirst}
            onClick={() => onMoveUp(index)}
          />
          <Button
            type="text"
            size="small"
            icon={<DownOutlined />}
            disabled={readOnly || isLast}
            onClick={() => onMoveDown(index)}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={readOnly}
            onClick={() => onRemove(index)}
          />
        </Space>
      }
    >
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item label="Label" style={{ marginBottom: 8 }}>
            <Input
              value={field.label}
              disabled={readOnly}
              onChange={(e) => onChange(index, 'label', e.target.value)}
              placeholder="Field label"
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Type" style={{ marginBottom: 8 }}>
            <Select
              value={field.fieldType}
              disabled={readOnly}
              onChange={(value) => onChange(index, 'fieldType', value)}
            >
              {Object.values(FormFieldTypes).map((t) => (
                <Select.Option key={t.key} value={t.key}>
                  {t.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Placeholder" style={{ marginBottom: 8 }}>
            <Input
              value={field.placeholder}
              disabled={readOnly}
              onChange={(e) => onChange(index, 'placeholder', e.target.value)}
              placeholder="Placeholder text"
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Required" style={{ marginBottom: 8 }}>
            <Checkbox
              checked={field.required}
              disabled={readOnly}
              onChange={(e) => onChange(index, 'required', e.target.checked)}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Help Text" style={{ marginBottom: 8 }}>
            <Input
              value={field.helpText}
              disabled={readOnly}
              onChange={(e) => onChange(index, 'helpText', e.target.value)}
              placeholder="Optional help text"
            />
          </Form.Item>
        </Col>
        {(field.fieldType === 'SELECT' ||
          field.fieldType === 'MULTI_SELECT') && (
          <Col span={12}>
            <Form.Item
              label="Options (comma separated)"
              style={{ marginBottom: 8 }}
            >
              <Input
                value={optionsText}
                disabled={readOnly}
                onChange={(e) => {
                  const value = e.target.value;
                  setOptionsText(value);
                  onChange(
                    index,
                    'options',
                    value
                      .split(',')
                      .map((o) => o.trim())
                      .filter(Boolean),
                  );
                }}
                onBlur={() =>
                  onChange(
                    index,
                    'options',
                    optionsText
                      .split(',')
                      .map((o) => o.trim())
                      .filter(Boolean),
                  )
                }
                placeholder="Option 1, Option 2, Option 3"
              />
              {optionsError && (
                <Text type="danger" style={{ display: 'block', marginTop: 4 }}>
                  {optionsError}
                </Text>
              )}
              {!optionsError && (
                <Text
                  type="secondary"
                  style={{ display: 'block', marginTop: 4 }}
                >
                  At least two options are recommended.
                </Text>
              )}
            </Form.Item>
          </Col>
        )}
      </Row>
    </Card>
  );
}

function EditReportScheduleTemplatePage() {
  const { _id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const isCreate = location.state?.mode === 'create';
  const cloneTemplate = location.state?.cloneTemplate;

  const [form] = Form.useForm();
  const [formFields, setFormFields] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useSingleSomethingUpdatesA10({
    variables: { _id },
    subscription: reportScheduleTemplateUpdatesSubscription,
    createAction: createRsTemplateAction,
    updateAction: updateRsTemplateAction,
    deleteAction: deleteRsTemplateAction,
    extractSomething: (resp) => resp?.data?.reportScheduleTemplateUpdates,
    queryId,
    skip: !_id || isCreate,
  });

  const { loading } = useOneM1({
    variables: { _id },
    query: reportScheduleTemplateQuery,
    extract: extractReportScheduleTemplate,
    fetchPolicy: 'network-only',
    updateAction: updateRsTemplateAction,
    queryId,
    skip: isCreate,
  });

  const template = useSingleSomethingA10(
    'reportScheduleTemplates',
    _id,
    queryId,
  );

  const isLockedTemplate = useMemo(
    () => !isCreate && !!template?.isLocked,
    [isCreate, template],
  );

  const lockReason =
    template?.lockReason ||
    'This template is locked because it is already in use. Clone it to make changes.';

  useEffect(() => {
    if (template && !isCreate) {
      form.setFieldsValue({
        name: template.name,
        desc: template.description,
        frequency: template.frequency,
      });
      setFormFields(
        (template.formFields || []).map((f) => ({
          ...f,
          fieldId: f.fieldId || generateUuid(),
        })),
      );
      setFieldErrors({});
    }
  }, [template, form, isCreate]);

  useEffect(() => {
    if (!isCreate || !cloneTemplate) {
      return;
    }

    form.setFieldsValue({
      name: cloneTemplate.name || '',
      desc: cloneTemplate.description || '',
      frequency: cloneTemplate.frequency,
    });

    setFormFields(
      (cloneTemplate.formFields || []).map((field, index) => ({
        ...field,
        fieldId: field.fieldId || generateUuid(),
        order: typeof field.order === 'number' ? field.order : index,
      })),
    );
    setFieldErrors({});
  }, [isCreate, cloneTemplate, form]);

  const [createTemplate] = useMutation(createReportScheduleTemplateMutation);
  const [updateTemplate] = useMutation(updateReportScheduleTemplateMutation);
  const [deleteTemplateMut] = useMutation(deleteReportScheduleTemplateMutation);

  const handleFieldChange = useCallback(
    (index, key, value) => {
      const fieldId = formFields?.[index]?.fieldId;
      setFormFields((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [key]: value };
        return updated;
      });
      if (fieldId) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[fieldId];
          return next;
        });
      }
    },
    [formFields],
  );

  const handleAddField = useCallback(() => {
    setFormFields((prev) => [
      ...prev,
      {
        fieldId: generateUuid(),
        fieldType: 'TEXT',
        label: '',
        required: false,
        options: [],
        placeholder: '',
        helpText: '',
        order: prev.length,
      },
    ]);
  }, []);

  const handleRemoveField = useCallback((index) => {
    setFormFields((prev) => {
      const removing = prev[index];
      if (removing?.fieldId) {
        setFieldErrors((current) => {
          const next = { ...current };
          delete next[removing.fieldId];
          return next;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const validateOptionsForSelectFields = useCallback((fields) => {
    const errors = {};
    fields.forEach((field, index) => {
      if (field.fieldType !== 'SELECT' && field.fieldType !== 'MULTI_SELECT') {
        return;
      }

      const options = (field.options || [])
        .map((option) => `${option || ''}`.trim())
        .filter(Boolean);
      if (options.length < 1) {
        errors[field.fieldId] =
          'Select and Multi Select fields require at least one option.';
      }

      if (!field.fieldId) {
        errors[`index-${index}`] =
          'Select and Multi Select fields require at least one option.';
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, []);

  const handleMoveUp = useCallback((index) => {
    if (index === 0) return;
    setFormFields((prev) => {
      const updated = [...prev];
      [updated[index - 1], updated[index]] = [
        updated[index],
        updated[index - 1],
      ];
      return updated;
    });
  }, []);

  const handleMoveDown = useCallback((index) => {
    setFormFields((prev) => {
      if (index >= prev.length - 1) return prev;
      const updated = [...prev];
      [updated[index], updated[index + 1]] = [
        updated[index + 1],
        updated[index],
      ];
      return updated;
    });
  }, []);

  const handleSave = useCallback(
    async (values) => {
      if (isLockedTemplate) {
        notification.warning({
          message: 'Template is locked',
          description: lockReason,
        });
        return;
      }
      const fieldsWithOrder = formFields.map((f, i) => ({
        fieldId: f.fieldId,
        fieldType: f.fieldType,
        label: f.label,
        required: f.required || false,
        options: f.options || [],
        placeholder: f.placeholder || '',
        helpText: f.helpText || '',
        order: i,
      }));

      if (!validateOptionsForSelectFields(fieldsWithOrder)) {
        notification.error({
          message: 'Template has invalid question options',
          description:
            'Each Select or Multi Select question must have at least one option.',
        });
        return;
      }

      setSaving(true);
      try {
        const templateData = {
          _id,
          name: values.name,
          description: values.desc || '',
          frequency: values.frequency,
          formFields: fieldsWithOrder,
        };

        if (isCreate) {
          await createTemplate({
            variables: { template: templateData },
          });
          notification.success({
            message: 'Created',
            description: 'Template created successfully',
          });
          history.replace(`/report-schedule-template/edit/${_id}`);
        } else {
          await updateTemplate({
            variables: { template: templateData },
          });
          notification.success({
            message: 'Saved',
            description: 'Template updated successfully',
          });
        }
      } catch (err) {
        console.error(err);
        notification.error({
          message: 'Error',
          description: err?.message || 'Failed to save template',
        });
      }
      setSaving(false);
    },
    [
      _id,
      formFields,
      isCreate,
      createTemplate,
      updateTemplate,
      history,
      isLockedTemplate,
      lockReason,
      validateOptionsForSelectFields,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (isLockedTemplate) {
      notification.warning({
        message: 'Template is locked',
        description: lockReason,
      });
      return;
    }
    setSaving(true);
    try {
      await deleteTemplateMut({ variables: { _id } });
      notification.success({
        message: 'Deleted',
        description: 'Template deleted successfully',
      });
      history.push('/report-schedule-templates');
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error',
        description: err?.message || 'Failed to delete template',
      });
    }
    setSaving(false);
  }, [_id, deleteTemplateMut, history, isLockedTemplate, lockReason]);

  const handleCloneTemplate = useCallback(() => {
    if (!template) return;
    const cloneId = generateUuid();
    history.push({
      pathname: `/report-schedule-template/edit/${cloneId}`,
      state: {
        mode: 'create',
        cloneTemplate: {
          name: `${template.name} (Copy)`,
          description: template.description || '',
          frequency: template.frequency,
          formFields: (template.formFields || []).map((field) => ({
            ...field,
            fieldId: generateUuid(),
          })),
        },
      },
    });
  }, [history, template]);

  const handlePreview = useCallback(() => {
    const values = form.getFieldsValue();
    setPreviewTemplate({
      name: values.name,
      frequency: values.frequency,
      formFields: formFields.map((f, i) => ({ ...f, order: i })),
    });
  }, [form, formFields]);

  const pageTitle = isCreate
    ? 'Create Scheduled Report Template'
    : 'Edit Scheduled Report Template';

  return (
    <>
      <CSPage containerStyle={{ maxWidth: 900 }} title={pageTitle}>
        <CSPageHeader
          title={pageTitle}
          topActions={[
            <Link key="back" to="/report-schedule-templates">
              <Button type="link" icon={<ArrowLeftOutlined />} size="large">
                Back to Templates
              </Button>
            </Link>,
            !isCreate && (
              <Button
                key="clone"
                type="link"
                icon={<CopyOutlined />}
                size="large"
                onClick={handleCloneTemplate}
              >
                Clone Template
              </Button>
            ),
          ]}
        />
        {isLockedTemplate && (
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            icon={<LockOutlined />}
            message="Template is locked"
            description={lockReason}
          />
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          style={{ maxWidth: 800 }}
          disabled={isLockedTemplate}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Template Name"
                rules={[{ required: true, message: 'Please enter a name' }]}
              >
                <Input placeholder="e.g. Weekly Progress Report" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="frequency"
                label="Frequency"
                rules={[
                  { required: true, message: 'Please select a frequency' },
                ]}
              >
                <Select placeholder="Select frequency">
                  {Object.values(ScheduleFrequencies).map((c) => (
                    <Select.Option key={c.key} value={c.key}>
                      {c.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="desc" label="Description">
            <Input.TextArea rows={3} placeholder="Template description" />
          </Form.Item>

          <Divider orientation="left">
            <Title level={5} style={{ margin: 0 }}>
              Form Fields
            </Title>
          </Divider>
          <Paragraph type="secondary">
            Configure the fields that will appear on reports using this
            template. Users will fill these out when submitting their reports.
          </Paragraph>

          {formFields.map((field, index) => (
            <FormFieldEditor
              key={field.fieldId}
              field={field}
              index={index}
              onChange={handleFieldChange}
              onRemove={handleRemoveField}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              isFirst={index === 0}
              isLast={index === formFields.length - 1}
              readOnly={isLockedTemplate}
              optionsError={fieldErrors[field.fieldId]}
            />
          ))}

          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={handleAddField}
            style={{ marginBottom: 24 }}
            disabled={isLockedTemplate}
          >
            Add Field
          </Button>

          <Divider />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                disabled={isLockedTemplate}
              >
                {isCreate ? 'Create Template' : 'Save Changes'}
              </Button>
              <Link to="/report-schedule-templates">
                <Button>Cancel</Button>
              </Link>
              <Button
                icon={<EyeOutlined />}
                // Explicit disabled={false}: the surrounding Form's disabled
                // prop (locked templates) propagates to Buttons via
                // DisabledContext.
                disabled={false}
                onClick={handlePreview}
              >
                Preview
              </Button>
              {!isCreate && isLockedTemplate && (
                <Button icon={<CopyOutlined />} onClick={handleCloneTemplate}>
                  Clone as New Template
                </Button>
              )}
            </Space>
            {!isCreate && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                loading={saving}
                disabled={isLockedTemplate}
              >
                Delete Template
              </Button>
            )}
          </div>
        </Form>
        <PreviewTemplateFormModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      </CSPage>
      <style jsx>{``}</style>
    </>
  );
}

export default EditReportScheduleTemplatePage;
